/**
 * API Client with axios interceptors
 * Handles authentication, token refresh, and error handling
 */

import axios, { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from "axios";
import { toast } from "react-hot-toast";
import { STORAGE_KEYS, API_CONFIG } from "@/constants";
import { navigateToLogin } from "@/utils/navigation";
import { queryClient } from "@/api/queryClient";
import { notifySessionEnded } from "@/hooks/useAuthSync";

// Type definitions for API responses
interface ApiErrorResponse {
  success?: boolean;
  error?: {
    message?: string;
    code?: string;
  } | string;
  message?: string;
}

interface ApiSuccessResponse<T = unknown> {
  success: boolean;
  data: T;
}

// Extended error type for API errors
interface ApiError extends Error {
  response?: AxiosResponse;
  isApiError?: boolean;
}

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "";

// Remove a (legacy) persisted token key from every web store. The current build
// never writes these, but older builds did — clear them on logout (defense in
// depth) so no stale tokens linger in storage.
const tokenRemove = (key: string): void => {
  try { localStorage.removeItem(key); } catch { /* ignore */ }
  try { sessionStorage.removeItem(key); } catch { /* ignore */ }
};


// Create axios instance
// Note: withCredentials is NOT set globally because it requires all APIs to have
// Access-Control-Allow-Credentials header. It's only used for auth refresh endpoint.
export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    "Content-Type": "application/json",
  },
});

// Token storage helpers.
//
// Security hardening: the access token is held in MEMORY only (never written to
// web storage) and the refresh token lives in an HttpOnly cookie set by the
// backend (never readable by JS). This shrinks the XSS blast radius — a script
// injection can no longer exfiltrate long-lived credentials from localStorage.
//
// On a full page reload the in-memory access token is lost; it is re-minted from
// the refresh cookie by `refreshSession()` (called on boot by the auth store
// and on 401 by the response interceptor).
let accessTokenMem: string | null = null;
let accessTokenExpiresAt = 0;

// This tab's own "remember me" mode. The shared key can be cleared by another
// tab's sign-out (clearTokens); an unticked tab must not then pass for a
// ticked one and escape the idle sign-out.
const readRememberMe = (): boolean => {
  try { return localStorage.getItem(STORAGE_KEYS.REMEMBER_ME) !== "false"; } catch { return true; }
};
let tabUnticked = !readRememberMe();

export const TokenStorage = {
  getAccessToken: () => accessTokenMem,
  // The refresh token is no longer accessible to JS (HttpOnly cookie).
  getRefreshToken: () => null as string | null,
  setRememberMe: (value: boolean) => {
    tabUnticked = !value;
    try { localStorage.setItem(STORAGE_KEYS.REMEMBER_ME, String(value)); } catch { /* ignore */ }
  },
  // An explicit choice in the shared key wins: only a login writes it, so a
  // "true" means a newer ticked login (even one this tab never heard about,
  // e.g. restored from the back/forward cache). With no key -- cleared by
  // another tab's sign-out -- this tab keeps its own unticked mode.
  isRememberMe: (): boolean => {
    let stored: string | null = null;
    try { stored = localStorage.getItem(STORAGE_KEYS.REMEMBER_ME); } catch { /* ignore */ }
    if (stored === "true") {
      tabUnticked = false;
      return true;
    }
    if (stored === "false") return false;
    return !tabUnticked;
  },
  // The same user logged in again in another tab: follow that login's choice
  // (its cookie is the one in use now).
  syncRememberMe: () => {
    tabUnticked = !readRememberMe();
  },
  // `refreshToken` is accepted for signature compatibility but intentionally
  // NOT persisted — it is carried by the HttpOnly cookie.
  setTokens: (accessToken: string, _refreshToken: string, expiresIn: number) => {
    accessTokenMem = accessToken;
    accessTokenExpiresAt = Date.now() + expiresIn * 1000;
  },
  // End the login in this tab only: shared keys stay for the other tabs.
  forgetAccessToken: () => {
    accessTokenMem = null;
    accessTokenExpiresAt = 0;
  },
  clearTokens: () => {
    accessTokenMem = null;
    accessTokenExpiresAt = 0;
    tabUnticked = false;
    try { localStorage.removeItem(STORAGE_KEYS.REMEMBER_ME); } catch { /* ignore */ }
    try { localStorage.removeItem(STORAGE_KEYS.LAST_ACTIVITY); } catch { /* ignore */ }
    try { sessionStorage.removeItem(STORAGE_KEYS.SESSION_ACTIVE); } catch { /* ignore */ }
    // Purge any tokens persisted by an older build (defense in depth).
    tokenRemove(STORAGE_KEYS.ACCESS_TOKEN);
    tokenRemove(STORAGE_KEYS.REFRESH_TOKEN);
    tokenRemove(STORAGE_KEYS.TOKEN_EXPIRES_AT);
  },
  getExpiresAt: () => accessTokenExpiresAt,
  isTokenExpired: () => Date.now() >= accessTokenExpiresAt,
};

/**
 * How a refresh went: "ok"; "refused" (the auth service said the login is
 * over: 400/401); or "failed" (network, timeout, rate limit, 5xx, or a 403,
 * which only a firewall or the CDN sends -- the login may well still be
 * valid).
 */
export type RefreshResult = "ok" | "refused" | "failed";

// Exchange the HttpOnly refresh cookie for a fresh access token (held in memory).
// The backend reads the refresh token from the cookie. For a "remember me"
// unticked login the body says so, which turns a persistent cookie from before
// that was enforced server-side into a browser-session one (it can only
// shorten a login). Single source of truth for boot, activity and 401 refresh.
export const refreshSession = async (): Promise<RefreshResult> => {
  // A newer login can land in this tab while the refresh is in flight
  // (changing the password revokes the old cookie and hands this tab a new
  // access token + cookie). A refusal then only concerns the old cookie.
  const tokenBefore = TokenStorage.getAccessToken();
  const newerLoginArrived = () => {
    const now = TokenStorage.getAccessToken();
    return !!now && now !== tokenBefore;
  };
  const attempt = async (timeout: number): Promise<RefreshResult | "lost"> => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/auth/v1/refresh`,
        TokenStorage.isRememberMe() ? {} : { remember_me: false },
        { withCredentials: true, timeout },
      );
      const { access_token, expires_in } = response.data ?? {};
      if (!access_token) return "failed";
      // A newer login landed while this (older) refresh was in flight: keep
      // it, don't overwrite it with the old login's token.
      if (newerLoginArrived()) return "ok";
      TokenStorage.setTokens(access_token, "", expires_in);
      return "ok";
    } catch (error) {
      if (!axios.isAxiosError(error)) return "failed";
      // No response (timeout, dropped connection): the server may have
      // rotated the token without our ever getting the new cookie.
      if (!error.response) return "lost";
      const status = error.response.status;
      if (status === 400 || status === 401) {
        return newerLoginArrived() ? "ok" : "refused";
      }
      return "failed";
    }
  };
  const first = await attempt(API_CONFIG.REFRESH_TIMEOUT_MS);
  if (first !== "lost") return first;
  // Retry at once with the same cookie: inside the server's grace window
  // it is still accepted. Sent much later it would look like a stolen
  // token and revoke the login in every tab.
  // Same timeout: a slow server is exactly when the first answer got lost, and
  // a retry that gives up early leaves a used cookie that later signs out.
  const second = await attempt(API_CONFIG.REFRESH_TIMEOUT_MS);
  return second === "lost" ? "failed" : second;
};


// Request interceptor - Add auth token (except for public endpoints)
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Skip auth for public endpoints
    const isPublicEndpoint = config.url?.includes('/public');

    // Skip auth for refresh endpoint to avoid infinite loop
    const isRefreshEndpoint = config.url?.includes('/auth/v1/refresh');

    const token = TokenStorage.getAccessToken();
    if (token && config.headers && !isPublicEndpoint && !isRefreshEndpoint) {
      // Check if token is expired before sending request
      // This prevents sending expired tokens and waiting for 401
      if (TokenStorage.isTokenExpired()) {
        // Token is expired - the response interceptor will handle refresh
        // But we still need to include the token for the 401 flow to work
        if (import.meta.env.DEV) console.debug('Token expired, will be refreshed on 401');
      }
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  },
);

// Response interceptor - Handle errors and token refresh
/**
 * The session is over in this tab: the refresh token was refused or refresh
 * kept failing. Tokens go, and so does everything React Query cached for
 * this user -- on a shared office machine the next person to log in would
 * otherwise see the last person's search hits and family details for up to
 * five minutes (change review 2026-09-15, MEDIUM 8). Only the menu Logout
 * cleared the cache before.
 */
export function endSession(): void {
  TokenStorage.clearTokens();
  queryClient.clear();
  // The auth store still says "logged in" at this point (it cannot be
  // imported here: it imports this module). Without telling it, Back from
  // the login page walked straight past ProtectedRoute into the previous
  // user's shell, name and role included. The other tabs are told too.
  notifySessionEnded();
  navigateToLogin();
}

let isRefreshing = false;
let refreshAttempts = 0;

let failedQueue: Array<{
  resolve: (value?: unknown) => void;
  reject: (reason?: unknown) => void;
  timestamp: number;
}> = [];

// Clean up stale queued requests
const cleanupStaleRequests = () => {
  const now = Date.now();
  failedQueue = failedQueue.filter((item) => {
    if (now - item.timestamp > API_CONFIG.QUEUE_TIMEOUT_MS) {
      item.reject(new Error('Token refresh timeout - request expired in queue'));
      return false;
    }
    return true;
  });
};

const processQueue = (
  error: AxiosError | null,
  token: string | null = null,
) => {
  // Capture and clear queue atomically to prevent race condition
  // where new items added during processing would be lost
  const queue = failedQueue;
  failedQueue = [];
  queue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
};

/**
 * Endpoints whose 401 means "these credentials are wrong", never "your
 * session expired". Their 401s must not trigger the token-refresh path.
 * Exported for unit tests.
 */
export function isCredentialEndpoint(url: string | undefined): boolean {
  if (!url) return false;
  return /\/api\/auth\/v1\/(login|password-reset-confirm)(\/|\?|$)/.test(url);
}

apiClient.interceptors.response.use(
  (response) => {
    // Skip processing for blob/text/arraybuffer responses (file downloads)
    if (response.config.responseType === "blob" || response.config.responseType === "text" || response.config.responseType === "arraybuffer") {
      return response;
    }

    // Check if response has success: false (error in 200 response)
    const responseData = response.data as ApiSuccessResponse | ApiErrorResponse;
    if (responseData && 'success' in responseData && responseData.success === false) {
      // Treat as error even though status is 200
      const errorData = responseData as ApiErrorResponse;
      const errorMessage = typeof errorData.error === 'object'
        ? errorData.error?.message
        : errorData.error || "Request failed";
      const error: ApiError = new Error(errorMessage || "Request failed");
      error.response = response;
      error.isApiError = true;
      return Promise.reject(error);
    }

    // Unwrap success response
    if (response.data?.success && response.data?.data) {
      return { ...response, data: response.data.data };
    }
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

    // Handle 401 Unauthorized - attempt token refresh (credential-check
    // 401s are surfaced to the caller instead; see isCredentialEndpoint).
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !isCredentialEndpoint(originalRequest.url)
    ) {
      // Clean up any stale queued requests
      cleanupStaleRequests();

      if (isRefreshing) {
        // Queue the request with timestamp for timeout tracking
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject, timestamp: Date.now() });
        })
          .then((token) => {
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
            }
            return apiClient(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      // Check for max refresh attempts to prevent infinite loops
      refreshAttempts++;
      if (refreshAttempts > API_CONFIG.MAX_REFRESH_ATTEMPTS) {
        if (import.meta.env.DEV) console.warn('Max refresh attempts exceeded');
        refreshAttempts = 0;
        isRefreshing = false;
        const refreshError = new Error('Max refresh attempts exceeded');
        processQueue(refreshError as unknown as AxiosError, null);
        toast.error("Session expired. Please log in again.");
        endSession();
        return Promise.reject(new Error('Max refresh attempts exceeded'));
      }

      try {
        // Exchange the HttpOnly refresh cookie for a new access token. No body
        // token is sent — the cookie is the source of truth. (Previously this
        // bailed out when no refresh token was in web storage; with cookie-only
        // auth we must always attempt the refresh.)
        const result = await refreshSession();
        if (result === "failed") {
          // Network, rate limit or 5xx: the login may well be fine. Fail this
          // request but keep the session -- ending it would sign every tab
          // out over a blip. It doesn't count towards the loop guard: this
          // request isn't retried.
          refreshAttempts = Math.max(0, refreshAttempts - 1);
          const failure = new Error("Token refresh failed (temporary)");
          processQueue(failure as unknown as AxiosError, null);
          toast.error("Couldn't reach the server. Please try again.", { id: "refresh-failed" });
          return Promise.reject(failure);
        }
        if (result === "refused") {
          throw new Error('Token refresh failed');
        }

        // Reset refresh attempts on success
        refreshAttempts = 0;
        const accessToken = TokenStorage.getAccessToken();
        processQueue(null, accessToken);

        if (originalRequest.headers && accessToken) {
          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        }

        return apiClient(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError as AxiosError, null);
        endSession();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // Handle other errors
    const errorData = error.response?.data as ApiErrorResponse | undefined;
    let errorMessage = "An unexpected error occurred";
    if (errorData) {
      if (typeof errorData.error === 'object' && errorData.error?.message) {
        errorMessage = errorData.error.message;
      } else if (errorData.message) {
        errorMessage = errorData.message;
      } else if (typeof errorData.error === 'string') {
        errorMessage = errorData.error;
      }
    } else if (error.message) {
      errorMessage = error.message;
    }

    // Show toast notification for errors (but not 401, 403, 404, isApiError, or _skipToast)
    // 404 is skipped because many queries check for resources that may not exist
    // _skipToast allows individual requests to suppress the toast (e.g., queries that handle errors inline)
    const apiError = error as ApiError;
    const requestConfig = error.config as InternalAxiosRequestConfig & { _skipToast?: boolean };
    const skipToast =
      error.response?.status === 401 ||
      error.response?.status === 403 ||
      error.response?.status === 404 ||
      apiError.isApiError ||
      requestConfig?._skipToast ||
      // A co-enrollment rule rejection is always handled inline by the form
      // that sent it (warning + explicit "... Anyway" override); a toast with
      // the raw machine code on top of that only confuses.
      errorMessage.includes("COENROLLMENT_REQUIRED") ||
      // Same for a class that filled up before payment: both checkout pages
      // explain it inline (waitlist offer / re-quote) without the raw code.
      errorMessage.includes("CLASS_FULL");

    if (!skipToast) {
      toast.error(errorMessage);
    }

    return Promise.reject(error);
  },
);

export default apiClient;
